import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const PLOTLY_SCRIPT_ID = "fleet-assistant-plotly-script";
const PLOTLY_SCRIPT_SRC = "https://cdn.plot.ly/plotly-2.35.2.min.js";
let loaderPromise = null;

function loadPlotly() {
  if (typeof window === "undefined") return Promise.reject(new Error("Window unavailable"));
  if (window.Plotly) return Promise.resolve(window.Plotly);
  if (loaderPromise) return loaderPromise;

  loaderPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(PLOTLY_SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.Plotly));
      existing.addEventListener("error", () => reject(new Error("Failed to load Plotly.")));
      return;
    }

    const script = document.createElement("script");
    script.id = PLOTLY_SCRIPT_ID;
    script.src = PLOTLY_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve(window.Plotly);
    script.onerror = () => reject(new Error("Failed to load Plotly."));
    document.head.appendChild(script);
  });

  return loaderPromise;
}

export default function PlotlyChart({
  figure,
  height = 260,
  config = {},
}) {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const [ready, setReady] = useState(false);

  const safeFigure = useMemo(
    () => ({
      data: Array.isArray(figure?.data) ? figure.data : [],
      layout: figure?.layout || {},
    }),
    [figure]
  );

  const resolvedConfig = useMemo(
    () => ({
      responsive: true,
      displayModeBar: "hover",
      displaylogo: false,
      scrollZoom: false,
      modeBarButtonsToRemove: [
        "lasso2d",
        "select2d",
        "autoScale2d",
        "hoverClosestCartesian",
        "hoverCompareCartesian",
        "toggleSpikelines",
      ],
      toImageButtonOptions: {
        format: "png",
        filename: "fleet-chart",
        scale: 2,
      },
      ...config,
    }),
    [config]
  );

  useEffect(() => {
    let mounted = true;
    loadPlotly()
      .then(() => {
        if (!mounted) return;
        setReady(true);
      })
      .catch((error) => {
        console.error("Plotly load error:", error);
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!ready || !containerRef.current || !window.Plotly) return;
    window.Plotly.react(containerRef.current, safeFigure.data, safeFigure.layout, resolvedConfig).catch(
      (error) => {
        console.error("Plotly render error:", error);
      }
    );
  }, [ready, safeFigure, resolvedConfig]);

  useEffect(() => {
    const node = containerRef.current;
    return () => {
      if (node && window.Plotly) {
        window.Plotly.purge(node);
      }
    };
  }, []);

  if (!ready) {
    return (
      <div
        style={{
          height,
          border: "1px solid rgba(15, 23, 42, 0.08)",
          borderRadius: 10,
          display: "grid",
          placeItems: "center",
          color: "#64748b",
          fontSize: 12,
        }}
      >
        {t("fleetAssistant.loadingChart")}
      </div>
    );
  }

  return <div ref={containerRef} style={{ width: "100%", height }} />;
}
